import { EcommerceEventGaEvents, GaEvents, GAEventBase } from '../../models/services/ga/ga';
import type { ConsentPreferences } from '../../models/services/cookiePro';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const initialiseDataLayer = () => (!window.dataLayer ? (window.dataLayer = []) : null);

/**
 * Universally resets GTM's abstract data model memory.
 * This instructs GTM's internal state tracker to clear out everything
 * from past events so the next push starts completely fresh.
 */
export const clearGTMState = () => {
  initialiseDataLayer();

  // 1. Clear standard variables by passing a resetting function to GTM's native command queue
  window.dataLayer.push(function (this: { reset?: () => void }) {
    if (typeof this.reset === 'function') {
      this.reset();
    }
  });

  // 2. Clear common ecommerce tracking objects explicitly,
  // as GTM sometimes keeps deep ecommerce arrays cached.
  // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
  window.dataLayer.push({ ecommerce: null });
};

const gaTagsBaseProperties = (): GAEventBase | undefined => {
  if (import.meta.env.VITE_ENABLE_GTM !== 'true') {
    return;
  }
  const environment = import.meta.env;
  initialiseDataLayer();
  return {
    version: 'WiB',
    domainId: environment.VITE_CLIENT_FRIENDLY_NAME.replaceAll(' ', '').toUpperCase(),
    brand: environment.VITE_BRAND_CODE.trim(),
    country: 'USA',
    customerType: 'G',
  };
};

export const gaPageView = <T extends GaEvents = GaEvents>(tags: T) => {
  if (import.meta.env.VITE_ENABLE_GTM !== 'true') {
    return;
  }

  const salesWidgetClicked =
    'salesWidgetClicked' in tags ? (tags.salesWidgetClicked ? 'Yes' : 'No') : undefined;

  const offerStatus = 'offerStatus' in tags ? (tags.offerStatus ? '1' : '0') : undefined;
  const baseProperties = gaTagsBaseProperties();

  // Clear everything before pushing the pageview data
  clearGTMState();

  window.dataLayer.push({
    ...tags,
    ...baseProperties,
    salesWidgetClicked,
    offerStatus,
    event: 'spa-pageview',
  });
};

export const applyGoogleAnalyticsConsentMode = (preferences?: ConsentPreferences): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const performanceCookiesEnabled =
    !preferences || preferences.status === 'error'
      ? true
      : preferences.status === 'ready'
        ? preferences.performance
        : true;

  initialiseDataLayer();
  // GTM does not create this wrapper. Queue commands even before it downloads.
  const gtag = (window.gtag ??= (...args: unknown[]) => {
    window.dataLayer.push(args);
  });

  gtag('consent', 'update', {
    analytics_storage: performanceCookiesEnabled ? 'granted' : 'denied',
  });
};

export const gaEvent = <T extends GaEvents = GaEvents>(tags: T) => {
  if (import.meta.env.VITE_ENABLE_GTM !== 'true') {
    return;
  }
  const baseProperties = gaTagsBaseProperties();

  const salesWidgetClicked =
    'salesWidgetClicked' in tags ? (tags.salesWidgetClicked ? 'Yes' : 'No') : undefined;

  const offerStatus = 'offerStatus' in tags ? (tags.offerStatus ? '1' : '0') : undefined;

  // Clear everything before pushing the event data
  clearGTMState();

  window.dataLayer.push({
    ...tags,
    ...baseProperties,
    salesWidgetClicked,
    offerStatus,
    event: 'event' in tags ? tags.event : 'genericGAEvent',
  });
};

export const gaECommerceEvent = <T extends EcommerceEventGaEvents = EcommerceEventGaEvents>(
  tags: T,
) => {
  if (import.meta.env.VITE_ENABLE_GTM !== 'true') {
    return;
  }

  // Clear everything before pushing the ecommerce data
  clearGTMState();

  window.dataLayer.push({
    ...tags,
  });
};
